import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Autocomplete from '@material-ui/lab/Autocomplete';
import axios from 'axios';
import clsx from 'clsx';
import React from 'react';
import { Controller as FormController, useForm, AutoComplete } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { WHITE_LABEL_PAGE } from '../../links';
import Cookies from 'js-cookie';

import sendContactUs from '../../services/contactUs';
import Loader from '../loader';
import Notification from '../notification';
import CustomDropDown from './CustomDropDown';
import Content from './Content';

const GATSBY_APP_API_URL = process.env.GATSBY_APP_API_URL;

const useStyles = makeStyles(theme => ({
  subTitle: {
    marginBottom: theme.spacing(7),
    fontWeight: 400,
    textAlign: 'center'
  },
  subTitle1: {
    marginTop: theme.spacing(5),
    fontWeight: 400,
    textAlign: 'center'
  },
  subTitle2: {
    marginBottom: theme.spacing(10),
    fontWeight: 400,
    textAlign: 'center'
  },
  formControl: {
    marginBottom: theme.spacing(2.5)
  },
  formControlLast: {
    marginBottom: theme.spacing(7)
  },
  formLabel: {
    fontSize: 14
  },
  formControlTextarea: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(14)
  },
  loader: {
    transform: 'translateY(-24px)'
  },
  menu1Form: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  menu1: {
    marginTop: 0,
    padding: 0,
    width: '60%'
  },
  menu1Sub: {
    marginTop: 0,
    padding: 0,
    width: '35%',
    backgroundColor: '#FFFFFF'
  },
  menu2: {
    marginTop: 0,
    padding: 0
  },
  menu3: {
    marginTop: 0,
    padding: 0
  },
  menu1Text: {
    marginTop: -15,
    marginBottom: 5
  },
  menu1SubText: {
    marginTop: -15,
    marginBottom: 5
  },
  menu2Text: {
    marginTop: 5,
    marginBottom: 5
  },
  menu3Text: {
    marginTop: 5,
    marginBottom: 50
  }
}));

const ContactForm = () => {

  const classes = useStyles();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = React.useState(false);
  const [notification, setNotification] = React.useState({
    open: false,
    type: '',
    message: ''
  });

  const [user, setUser] = React.useState(null);
  const [isAuthenticated, setAuthenticated] = React.useState(false);
  const [expand, setExpand] = React.useState({
    first: false, second: false, third: false
  })

  const [wantArFor, setWantArFor] = React.useState([]);
  const [methodsAr, setMethodsAr] = React.useState([]);
  const [appearWhen, setAppearWhen] = React.useState([]);
  const [format, setFormat] = React.useState(false);

  const validationSchema = yup.object().shape({
    name: yup.string().required('Name is required'),
    email: yup
      .string()
      .email('Email must be a valid email')
      .required('Email is required'),
    message: (wantArFor.length == 0 || wantArFor[0] == 'Other') ? yup.string().required('Description is required') : null
  });

  const { handleSubmit, control, errors, reset } = useForm({
    validationSchema,
    defaultValues: {
      name: '',
      businessName: '',
      email: '',
      phoneNumber: '',
      message: ''
    }
  });

  React.useEffect(() => {
    const token = Cookies.get('token');
    setAuthenticated(!!Cookies.get('token'));
    if (token) {
      axios
        .get(GATSBY_APP_API_URL + `/user/me`, {
          headers: {
            'x-authorization': `Bearer ${token}`,
            "Access-Control-Allow-Origin": "*"
          },
          mode: 'cors'
        })
        .then(response => {
          if (response.data && response.data.success === true) {
            console.log("logged in user data in contact us page: \n ", response.data.user)
            setUser(response.data.user);
          }
        })
        .catch(error => {
          console.log("error during the get the user information from the token: ", error);
        });
    }
  }, []);

  React.useEffect(() => {
    if (isAuthenticated && user) {
      const payload = {
        "Email": user.email,
        "Identity": user.id,
        "Name": user.firstName
      }
      window.clevertap.event.push("Contact us", payload)
    }
  }, [isAuthenticated, user])

  const onSubmit = data => {
    setIsLoading(true);
    setFormat(false)
    setExpand({first: false, second: false, third: false});
    data.wantArFor = wantArFor;
    data.methodsAr = methodsAr;
    data.appearWhen = appearWhen;
    
    sendContactUs(data)
      .then(result => {
        setNotification(true);
        if (result.success) {
          return setNotification({
            open: true,
            type: 'success',
            message: 'Thank you for your inquiry!'
          });
        }
        return setNotification({
          open: true,
          type: 'error',
          message: result.message
        });
      })
      .catch(error => {
        console.error(error);
      })
      .finally(() => {
        reset();
        setIsLoading(false);
        setFormat(true);
      });
  };

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setNotification(false);
  };
  
  return (
    <>
      {notification.open && (
        <Notification
          open={notification.open}
          onClose={handleClose}
          type={notification.type}
          message={notification.message}
        />
      )}

      {/* Form */}
      <div className={classes.formWrap}>
        <Typography className={classes.subTitle} variant="h4">
          {t('contactUsPage.title')}
        </Typography>
        <form
          className={classes.form}
          onSubmit={handleSubmit(onSubmit)}
          noValidate
        >
          {/* Your name */}
          <FormController
            as={TextField}
            control={control}
            defaultValue=""
            type="text"
            name="name"
            label={t('contactUsPage.name')}
            fullWidth
            className={classes.formControl}
            disabled={isLoading}
            error={!!errors.name}
            helperText={errors.name ? errors.name.message : ''}
            FormHelperTextProps={{
              classes: {
                root: classes.helper
              }
            }}
          />

          {/* Business name  */}
          <FormController
            as={TextField}
            control={control}
            defaultValue=""
            type="text"
            name="businessName"
            label={t('contactUsPage.businessName')}
            fullWidth
            className={classes.formControl}
            disabled={isLoading}
          />

          {/* Email */}
          <FormController
            as={TextField}
            control={control}
            defaultValue=""
            type="email"
            name="email"
            label={t('contactUsPage.email')}
            fullWidth
            className={classes.formControl}
            disabled={isLoading}
            error={!!errors.email}
            helperText={errors.email ? errors.email.message : ''}
            FormHelperTextProps={{
              classes: {
                root: classes.helper
              }
            }}
          />

          {/* Phone number */}
          <FormController
            as={TextField}
            control={control}
            defaultValue=""
            type="text"
            name="phoneNumber"
            label={t('contactUsPage.phoneNumber')}
            fullWidth
            className={clsx(classes.formControl, classes.formControlLast)}
            disabled={isLoading}
          />

          {/* Title for drop down */}

          <Typography className={classes.subTitle1} variant="h4">
            {t('contactUsPage.subTitle')}
          </Typography>
          <Typography className={classes.subTitle2} variant="h4">
            {t('contactUsPage.subTitleWith')}
          </Typography>

          {/* DropDownMenu */}
          <CustomDropDown
            title = {t('contactUsPage.wantArForTitle')}
            data = {Content.MENU_ONE_DATA}
            expand = {expand.first}
            format = {format}
            onClickDropDown = {(value) => {
              setExpand({first: true, second: false, third: false})
              setWantArFor(value.filter( el => el !== null))
            }}
          />

          <CustomDropDown
            title = {t('contactUsPage.methodsArTitle')}
            data = {Content.MENU_TWO_DATA}
            expand = {expand.second}
            format = {format}
            onClickDropDown = {(value) => {
              setExpand({first: false, second: true, third: false})
              setMethodsAr(value.filter( el => el !== null))
            }}
          />

          <CustomDropDown
            title = {t('contactUsPage.appearWhenTitle')}
            data = {Content.MENU_THREE_DATA}
            expand = {expand.third}
            format = {format}
            onClickDropDown = {(value) => {
              setExpand({first: false, second: false, third: true})
              setAppearWhen(value.filter( el => el !== null))
            }}
          />

          {/* Message */}
          <FormController
            as={TextField}
            control={control}
            defaultValue=""
            type="text"
            name="message"
            label={
              (wantArFor.length == 0 || wantArFor[0] == "Other") ?
              t('contactUsPage.message') : t('contactUsPage.messageOptional')
            }
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            className={classes.formControlTextarea}
            disabled={isLoading}
            error={!!errors.message}
            helperText={errors.message ? errors.message.message : ''}
            FormHelperTextProps={{
              classes: {
                root: classes.helper
              }
            }}
          />

          {/* Loader */}
          {isLoading && (
            <Loader className={classes.loader} message="Sending ..." />
          )}

          {/* Submit button */}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isLoading}
          >
            {t('contactUsPage.submitButton')}
          </Button>
        </form>
      </div>
    </>
  );
};

export default ContactForm;
